import { loadStripe } from "@stripe/stripe-js";

import { callFunctionRaw, resolveFunctionAccessToken } from "@/lib/callFunction";
import { resolveValidatedSession } from "@/lib/authSession.js";

const STRIPE_TEST_PUBLISHABLE_KEY = String(import.meta.env.VITE_STRIPE_PK_TEST || "").trim();
const STRIPE_LIVE_PUBLISHABLE_KEY = String(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "").trim();
const APP_ENV = String(import.meta.env.VITE_APP_ENV || "").trim().toLowerCase();

const stripePromiseCache = new Map();

function getRuntimeHostname() {
  if (typeof window === "undefined") return "";
  return String(window.location.hostname || "").trim().toLowerCase();
}

function isStagingCheckoutHost(hostname = getRuntimeHostname()) {
  return (
    hostname === "staging.afrimed.tech" ||
    hostname.endsWith("--afrimed-staging.netlify.app") ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  );
}

function resolveCheckoutPublishableKey({ livemode = false } = {}) {
  if (APP_ENV === "staging" || isStagingCheckoutHost()) {
    return STRIPE_TEST_PUBLISHABLE_KEY;
  }
  if (livemode) {
    return STRIPE_LIVE_PUBLISHABLE_KEY || STRIPE_TEST_PUBLISHABLE_KEY;
  }
  return STRIPE_TEST_PUBLISHABLE_KEY || STRIPE_LIVE_PUBLISHABLE_KEY;
}

export function isCardPaymentMethod(methodId) {
  const clean = String(methodId || "").trim().toLowerCase();
  return clean === "credit" || clean === "debit" || clean === "card";
}

export function getCheckoutStripe({ livemode = false, publishableKey: preferredKey = "" } = {}) {
  const publishableKey =
    String(preferredKey || "").trim() || resolveCheckoutPublishableKey({ livemode });
  if (!publishableKey) return Promise.resolve(null);
  if (!stripePromiseCache.has(publishableKey)) {
    stripePromiseCache.set(publishableKey, loadStripe(publishableKey));
  }
  return stripePromiseCache.get(publishableKey);
}

export function clearCheckoutStripeCache() {
  stripePromiseCache.clear();
}

export async function createAppointmentPaymentIntent({
  amountCents,
  currency = "ZAR",
  customerEmail = "",
  doctorUserId = "",
  appointmentId = "",
  serviceName = "",
  bookingDate = "",
  bookingTime = "",
  savePaymentMethod = false,
  metadata = {},
} = {}) {
  let accessToken = await resolveFunctionAccessToken({
    refreshIfMissing: true,
    forceRefresh: false,
  });

  if (!String(accessToken || "").trim()) {
    const session = await resolveValidatedSession();
    accessToken = String(session?.access_token || "").trim();
  }

  if (!String(accessToken || "").trim()) {
    throw new Error("Session expired. Please sign in again.");
  }

  return callFunctionRaw(
    "create-payment-intent",
    {
      amount_cents: Number(amountCents || 0),
      currency: String(currency || "ZAR").trim().toLowerCase(),
      customer_email: String(customerEmail || "").trim(),
      doctor_user_id: String(doctorUserId || "").trim(),
      appointment_id: String(appointmentId || "").trim(),
      service_name: String(serviceName || "").trim(),
      booking_date: String(bookingDate || "").trim(),
      booking_time: String(bookingTime || "").trim(),
      save_payment_method: Boolean(savePaymentMethod),
      meta: metadata || {},
    },
    {
      accessToken,
      timeoutMs: 15000,
      preferFreshSession: false,
    },
  );
}

export default {
  createAppointmentPaymentIntent,
  getCheckoutStripe,
  isCardPaymentMethod,
};
